import React from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'

import useDefer, { Status } from '../../../common/hooks/useDefer'
import { PartnerDetailsWidget } from '../../../lib/partner-details-widget'
import { PartnerAdminsWidget } from '../../../lib/partner-admins-widget'
import { PartnerOrganisationsWidget } from '../../../lib/partner-organisations-widget'
import loadPartner from '../../../lib/loadPartner'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { Button } from '../../../lib/button'
import { loadPartnerUsers } from '../../../lib/load-partner-users'
import { deletePartner as deletePartnerRequest } from '../../../lib/delete-partner'
import loadPartnerCompanies from '../../../lib/loadPartnerCompanies'
import { WidgetLoadingState } from '../../../lib/widget-loading-state'
import useText from '../../../common/hooks/useText'

const SActions = styled.div`
    display: flex;
    justify-content: flex-end;
    padding-bottom: 20px;
    gap: 4px;
`

export type PartnerPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export const PartnerPage = React.memo(function PartnerPage({ className, style }: PartnerPageProps): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const { id: partnerId } = useParams()

    const { value: partner } = useDefer(
        () => {
            if (!partnerId) return
            return loadPartner(partnerId)
        },
        [partnerId],
        []
    )

    const {
        value: users,
        status: usersLoadingStatus,
        execute: reloadUsers,
    } = useDefer(
        () => {
            if (!partnerId) return
            return loadPartnerUsers(partnerId)
        },
        [partnerId],
        []
    )

    const {
        value: companies,
        status: companiesLoadingStatus,
        execute: reloadCompanies,
    } = useDefer(
        () => {
            if (!partnerId) return
            return loadPartnerCompanies(+partnerId)
        },
        [partnerId],
        []
    )

    const { execute: deletePartner } = useDefer(() => {
        if (!partnerId) return
        if (!confirm(text('msgDeletePartnerConfirmation'))) return

        return deletePartnerRequest(partnerId)
            .then(() => navigate('/partners'))
            .catch(() => alert(text('msgDeletePartnerFailed')))
    }, [partnerId])

    return (
        <PageLayoutContainer className={cn(className)} style={style}>
            <SActions>
                <Button
                    small
                    loading={!partner || usersLoadingStatus === Status.PENDING}
                    title={text('btnDeletePartner')}
                    onClick={deletePartner}
                    tooltip={
                        users?.length
                            ? text('msgCannotDeletePartnerThatHasUsers')
                            : companies?.length
                              ? text('msgCannotDeletePartnerThatHasOrganisations')
                              : ''
                    }
                    disabled={
                        !partner ||
                        usersLoadingStatus === Status.PENDING ||
                        !!users?.length ||
                        companiesLoadingStatus === Status.PENDING ||
                        !!companies?.length
                    }
                />
            </SActions>
            {companiesLoadingStatus !== Status.PENDING && partner && (
                <>
                    <PartnerDetailsWidget partner={partner} />
                    <PartnerAdminsWidget reload={reloadUsers} partnerUsers={users} partnerId={partner.id.toString()} />
                    <PartnerOrganisationsWidget reload={reloadCompanies} organisations={companies} partner={partner} />
                </>
            )}
            {companiesLoadingStatus === Status.PENDING && (
                <>
                    <WidgetLoadingState title height="437px" />
                    <WidgetLoadingState title height="156px" />
                    <WidgetLoadingState title headerButton height="338px" />
                </>
            )}
        </PageLayoutContainer>
    )
})
